import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { z } from "zod";
import { X } from "lucide-react";
import { parseDateAsLocal } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PhoneInput } from "@/components/ui/phone-input";

const memberSchema = z.object({
  full_name: z.string().min(1).max(100),
  address: z.string().max(500).optional(),
  phone: z.string().max(20).optional(),
  email: z.string().email().max(255),
  number_of_kids: z.number().min(0).max(50),
  date_of_birth: z.string().optional(),
});

interface FamilyMember {
  id: string;
  full_name: string;
  relationship_type: 'spouse' | 'child';
  phone?: string;
  email?: string;
  date_of_birth?: string;
  is_member?: boolean;
}

interface MemberProfileProps {
  memberData: any;
  onUpdate: () => void;
}

export const MemberProfile = ({ memberData, onUpdate }: MemberProfileProps) => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: memberData.full_name || "",
    address: memberData.address || "",
    phone: memberData.phone || "",
    email: memberData.email || "",
    number_of_kids: memberData.number_of_kids || 0,
    date_of_birth: memberData.date_of_birth || "",
  });
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [spouseData, setSpouseData] = useState({
    name: "",
    phone: "",
    email: "",
    date_of_birth: "",
  });
  const [childrenData, setChildrenData] = useState<Array<{
    id?: string;
    name: string;
    phone: string;
    email: string;
    date_of_birth: string;
  }>>([]);

  useEffect(() => {
    loadFamilyMembers();
  }, [memberData.id, memberData]);

  const loadFamilyMembers = async () => {
    try {
      // Load manually added family members
      let { data: familyData, error: familyError } = await supabase
        .from("family_members")
        .select("*")
        .eq("member_id", memberData.id);

      if (familyError) throw familyError;

      // Load family relationships to check if they're church members
      const { data: relationshipsData, error: relationshipsError } = await supabase
        .from("family_relationships")
        .select(`
          *,
          related_member:members!family_relationships_related_member_id_fkey(*)
        `)
        .eq("member_id", memberData.id)
        .in("relationship_type", ["spouse", "child"]);

      if (relationshipsError) throw relationshipsError;

      // Auto-sync: If we have relationships, ensure all are in family_members
      if (relationshipsData && relationshipsData.length > 0) {
        const insertPromises = [];
        
        for (const rel of relationshipsData) {
          if (rel.related_member) {
            // Check if this person already exists in family_members by ID or name
            const existsInFamily = familyData?.some(
              (fm) => fm.full_name.toLowerCase() === rel.related_member.full_name.toLowerCase() &&
                      fm.relationship_type === rel.relationship_type
            );
            
            if (!existsInFamily) {
              // Queue insert for missing family member
              insertPromises.push(
                supabase.from("family_members").insert({
                  member_id: memberData.id,
                  full_name: rel.related_member.full_name,
                  relationship_type: rel.relationship_type,
                  phone: rel.related_member.phone,
                  email: rel.related_member.email,
                  date_of_birth: rel.related_member.date_of_birth,
                })
              );
            }
          }
        }
        
        // Wait for all inserts to complete
        if (insertPromises.length > 0) {
          await Promise.all(insertPromises);
          
          // Reload family data after sync
          const { data: updatedFamilyData } = await supabase
            .from("family_members")
            .select("*")
            .eq("member_id", memberData.id);
            
          familyData = updatedFamilyData;
        }
      }

      // Create a map of relationship members by name to check membership status
      const memberRelationships = new Map();
      relationshipsData?.forEach((rel: any) => {
        if (rel.related_member) {
          const key = `${rel.related_member.full_name.toLowerCase()}_${rel.relationship_type}`;
          memberRelationships.set(key, rel);
        }
      });

      if (familyData) {
        // Mark each family member as member or non-member
        const enrichedData = familyData.map((fm) => {
          const key = `${fm.full_name.toLowerCase()}_${fm.relationship_type}`;
          return {
            ...fm,
            is_member: memberRelationships.has(key),
          };
        }) as FamilyMember[];

        setFamilyMembers(enrichedData);
        const spouse = enrichedData.find((fm) => fm.relationship_type === "spouse");
        const children = enrichedData.filter((fm) => fm.relationship_type === "child");
        
        setSpouseData({
          name: spouse?.full_name || "",
          phone: spouse?.phone || "",
          email: spouse?.email || "",
          date_of_birth: spouse?.date_of_birth || "",
        });
        
        setChildrenData(children.map((c) => ({
          id: c.id,
          name: c.full_name,
          phone: c.phone || "",
          email: c.email || "",
          date_of_birth: c.date_of_birth || "",
        })));
      }
    } catch (error: any) {
      console.error("Error loading family members:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const validated = memberSchema.parse(formData);

      // Update member data
      const { error: memberError } = await supabase
        .from("members")
        .update(validated)
        .eq("id", memberData.id);

      if (memberError) throw memberError;

      // Handle spouse
      const existingSpouse = familyMembers.find((fm) => fm.relationship_type === "spouse");
      
      if (spouseData.name?.trim()) {
        const spousePayload = {
          full_name: spouseData.name.trim(),
          phone: spouseData.phone?.trim() || null,
          email: spouseData.email?.trim() || null,
          date_of_birth: spouseData.date_of_birth || null,
        };
        
        if (existingSpouse) {
          // Update existing spouse
          const { error } = await supabase
            .from("family_members")
            .update(spousePayload)
            .eq("id", existingSpouse.id);
          if (error) throw error;
        } else {
          // Insert new spouse
          const { error } = await supabase
            .from("family_members")
            .insert({
              member_id: memberData.id,
              relationship_type: "spouse",
              ...spousePayload,
            });
          if (error) throw error;
        }
      } else if (existingSpouse) {
        // Remove spouse if name is empty
        const { error } = await supabase
          .from("family_members")
          .delete()
          .eq("id", existingSpouse.id);
        if (error) throw error;
      }

      // Handle children
      const existingChildren = familyMembers.filter((fm) => fm.relationship_type === "child");
      const existingChildIds = new Set(existingChildren.map(c => c.id));
      const updatedChildIds = new Set(childrenData.filter(c => c.id).map(c => c.id));
      
      // Remove children that are no longer in the list
      for (const child of existingChildren) {
        if (!updatedChildIds.has(child.id)) {
          const { error } = await supabase
            .from("family_members")
            .delete()
            .eq("id", child.id);
          if (error) throw error;
        }
      }

      // Add or update children
      for (const childData of childrenData) {
        if (!childData.name?.trim()) continue;
        
        const childPayload = {
          full_name: childData.name.trim(),
          phone: childData.phone?.trim() || null,
          email: childData.email?.trim() || null,
          date_of_birth: childData.date_of_birth || null,
        };
        
        if (childData.id && existingChildIds.has(childData.id)) {
          // Update existing child
          const { error } = await supabase
            .from("family_members")
            .update(childPayload)
            .eq("id", childData.id);
          if (error) throw error;
        } else {
          // Insert new child
          const { error } = await supabase
            .from("family_members")
            .insert({
              member_id: memberData.id,
              relationship_type: "child",
              ...childPayload,
            });
          if (error) throw error;
        }
      }

      toast({
        title: t("member.profile.success"),
        description: t("member.profile.updateSuccess"),
      });

      setEditing(false);
      await loadFamilyMembers();
      onUpdate();
    } catch (error: any) {
      toast({
        title: t("member.profile.error"),
        description: error.message || t("member.profile.updateError"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNumberOfKidsChange = (value: number) => {
    const newCount = Math.max(0, value);
    setFormData({ ...formData, number_of_kids: newCount });
    
    // Adjust children data array
    if (newCount > childrenData.length) {
      const newChildren = Array(newCount - childrenData.length).fill(null).map(() => ({
        name: "",
        phone: "",
        email: "",
        date_of_birth: "",
      }));
      setChildrenData([...childrenData, ...newChildren]);
    } else if (newCount < childrenData.length) {
      setChildrenData(childrenData.slice(0, newCount));
    }
  };

  const updateChildData = (index: number, field: string, value: string) => {
    const newData = [...childrenData];
    newData[index] = { ...newData[index], [field]: value || "" };
    setChildrenData(newData);
  };

  const calculateAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = parseDateAsLocal(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatMonthDay = (dateOfBirth: string) => {
    if (!dateOfBirth) return "";
    const date = parseDateAsLocal(dateOfBirth);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  const removeChild = async (index: number) => {
    const childData = childrenData[index];
    const existingChild = familyMembers.find(
      (fm) => fm.relationship_type === "child" && fm.full_name === childData.name
    );

    if (existingChild) {
      try {
        const { error } = await supabase
          .from("family_members")
          .delete()
          .eq("id", existingChild.id);
        
        if (error) throw error;
      } catch (error: any) {
        toast({
          title: "Error",
          description: "Failed to remove child",
          variant: "destructive",
        });
        return;
      }
    }

    const newData = childrenData.filter((_, i) => i !== index);
    setChildrenData(newData);
    setFormData({ ...formData, number_of_kids: newData.length });
    await loadFamilyMembers();
  };

  const removeSpouse = async () => {
    const existingSpouse = familyMembers.find((fm) => fm.relationship_type === "spouse");
    
    if (existingSpouse) {
      try {
        const { error } = await supabase
          .from("family_members")
          .delete()
          .eq("id", existingSpouse.id);
        
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Spouse removed successfully",
        });
      } catch (error: any) {
        toast({
          title: "Error",
          description: "Failed to remove spouse",
          variant: "destructive",
        });
        return;
      }
    }

    setSpouseData({ name: "", phone: "", email: "", date_of_birth: "" });
    await loadFamilyMembers();
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      active: "default",
      pending: "secondary",
      inactive: "destructive",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>{t("member.profile.title")}</CardTitle>
            <CardDescription>{t("member.profile.description")}</CardDescription>
          </div>
          <div className="flex gap-2 items-center">
            {getStatusBadge(memberData.membership_status)}
            {!editing && (
              <Button onClick={() => setEditing(true)}>{t("member.profile.edit")}</Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {editing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="full_name">{t("member.profile.fullName")}</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) =>
                  setFormData({ ...formData, full_name: e.target.value })
                }
                required
              />
            </div>

            <div>
              <Label htmlFor="email">{t("member.profile.email")}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
            </div>

            <div>
              <Label htmlFor="phone">{t("member.profile.phone")}</Label>
              <PhoneInput
                id="phone"
                value={formData.phone}
                onChange={(value) =>
                  setFormData({ ...formData, phone: value })
                }
              />
            </div>

            <div>
              <Label htmlFor="address">{t("member.profile.address")}</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
            </div>

            <div>
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) =>
                  setFormData({ ...formData, date_of_birth: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Spouse Information</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={spouseData.name}
                  onChange={(e) => setSpouseData({ ...spouseData, name: e.target.value })}
                  placeholder="Spouse name"
                />
                <PhoneInput
                  value={spouseData.phone}
                  onChange={(value) => setSpouseData({ ...spouseData, phone: value })}
                  placeholder="Phone"
                />
                <Input
                  type="email"
                  value={spouseData.email}
                  onChange={(e) => setSpouseData({ ...spouseData, email: e.target.value })}
                  placeholder="Email"
                />
                <Input
                  type="date"
                  value={spouseData.date_of_birth}
                  onChange={(e) => setSpouseData({ ...spouseData, date_of_birth: e.target.value })}
                  placeholder="Date of Birth"
                />
              </div>
              {spouseData.name && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove Spouse
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove Spouse</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to remove {spouseData.name}? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={removeSpouse}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Remove
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>

            <div>
              <Label htmlFor="number_of_kids">{t("member.profile.numberOfKids")}</Label>
              <Input
                id="number_of_kids"
                type="number"
                min="0"
                value={formData.number_of_kids}
                onChange={(e) => handleNumberOfKidsChange(parseInt(e.target.value) || 0)}
              />
            </div>

            {formData.number_of_kids > 0 && (
              <div className="space-y-4">
                <Label>Children Information</Label>
                {Array.from({ length: formData.number_of_kids }).map((_, index) => (
                  <div key={index} className="p-4 border rounded-lg space-y-2">
                    <div className="flex justify-between items-center mb-2">
                      <Label className="font-semibold">Child {index + 1}</Label>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove Child</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to remove {childrenData[index]?.name || `Child ${index + 1}`}? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => removeChild(index)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={childrenData[index]?.name || ""}
                        onChange={(e) => updateChildData(index, "name", e.target.value)}
                        placeholder="Name"
                      />
                      <PhoneInput
                        value={childrenData[index]?.phone || ""}
                        onChange={(value) => updateChildData(index, "phone", value)}
                        placeholder="Phone"
                      />
                      <Input
                        type="email"
                        value={childrenData[index]?.email || ""}
                        onChange={(e) => updateChildData(index, "email", e.target.value)}
                        placeholder="Email"
                      />
                      <Input
                        type="date"
                        value={childrenData[index]?.date_of_birth || ""}
                        onChange={(e) => updateChildData(index, "date_of_birth", e.target.value)}
                        placeholder="Date of Birth"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Button type="submit" disabled={loading}>
                {loading ? t("member.profile.saving") : t("member.profile.saveChanges")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditing(false)}
              >
                {t("member.profile.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground">{t("member.profile.fullName")}</Label>
              <p className="font-medium">{memberData.full_name}</p>
            </div>

            <div>
              <Label className="text-muted-foreground">{t("member.profile.email")}</Label>
              <p className="font-medium">{memberData.email}</p>
            </div>

            <div>
              <Label className="text-muted-foreground">{t("member.profile.phone")}</Label>
              <p className="font-medium">{memberData.phone || t("member.profile.notProvided")}</p>
            </div>

            <div>
              <Label className="text-muted-foreground">{t("member.profile.address")}</Label>
              <p className="font-medium">{memberData.address || t("member.profile.notProvided")}</p>
            </div>

            {formData.date_of_birth && (
              <>
                <div>
                  <Label className="text-muted-foreground">Date of Birth</Label>
                  <p className="font-medium">{formatMonthDay(formData.date_of_birth)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Age</Label>
                  <p className="font-medium">{calculateAge(formData.date_of_birth)}</p>
                </div>
              </>
            )}

            <div>
              <Label className="text-muted-foreground">{t("member.profile.numberOfKids")}</Label>
              <p className="font-medium">{memberData.number_of_kids}</p>
            </div>

            {spouseData.name && (
              <div className="col-span-2">
                <div className="flex items-center gap-2 mb-1">
                  <Label className="text-muted-foreground">Spouse</Label>
                  {familyMembers.find((fm) => fm.relationship_type === "spouse")?.is_member ? (
                    <Badge variant="default" className="text-xs">Member</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Non-Member</Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name: </span>
                    <span className="font-medium">{spouseData.name}</span>
                  </div>
                  {spouseData.phone && (
                    <div>
                      <span className="text-muted-foreground">Phone: </span>
                      <span className="font-medium">{spouseData.phone}</span>
                    </div>
                  )}
                  {spouseData.email && (
                    <div>
                      <span className="text-muted-foreground">Email: </span>
                      <span className="font-medium">{spouseData.email}</span>
                    </div>
                  )}
                  {spouseData.date_of_birth && (
                    <>
                      <div>
                        <span className="text-muted-foreground">DOB: </span>
                        <span className="font-medium">{formatMonthDay(spouseData.date_of_birth)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Age: </span>
                        <span className="font-medium">{calculateAge(spouseData.date_of_birth)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {childrenData.length > 0 && (
              <div className="col-span-2">
                <Label className="text-muted-foreground">Children</Label>
                <div className="space-y-2 mt-1">
                  {childrenData.map((child, index) => {
                    const childFamilyMember = familyMembers.find(
                      (fm) => fm.relationship_type === "child" && fm.full_name === child.name
                    );
                    return (
                      <div key={index} className="p-2 border rounded text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-muted-foreground">
                            Child {index + 1}
                          </span>
                          {childFamilyMember?.is_member ? (
                            <Badge variant="default" className="text-xs">Member</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Non-Member</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-muted-foreground">Name: </span>
                            <span className="font-medium">{child.name}</span>
                          </div>
                          {child.phone && (
                            <div>
                              <span className="text-muted-foreground">Phone: </span>
                              <span className="font-medium">{child.phone}</span>
                            </div>
                          )}
                          {child.email && (
                            <div>
                              <span className="text-muted-foreground">Email: </span>
                              <span className="font-medium">{child.email}</span>
                            </div>
                          )}
                          {child.date_of_birth && (
                            <>
                              <div>
                                <span className="text-muted-foreground">DOB: </span>
                                <span className="font-medium">{formatMonthDay(child.date_of_birth)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Age: </span>
                                <span className="font-medium">{calculateAge(child.date_of_birth)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label className="text-muted-foreground">{t("member.profile.memberSince")}</Label>
              <p className="font-medium">
                {new Date(memberData.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
